import { TableProps } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';

import TableWrapper from '@/components/atoms/TableWrapper';
import CustomTable from '@/components/atoms/CustomTable';
import { Filters } from './Filters';
import {
  setDisputeFilters,
  useDisputeFilters,
} from '@/store/slices/reportFiltersSlice';
import { RequestDetailsDrawer } from './RequestDetailsDrawer';
import { useGetDisputesReportQuery } from '@/store/services/reports';
import {
  setModalId,
  setPage,
  usePublicState,
} from '@/store/slices/publicSlice';
import Helpers from '@/lib/helpers';

export interface DisputeItem {
  disputeId: string;
  disputeSerial: string;
  orderId: string;
  orderSerial: string;
  orderBudget: number;
  orderMethod: string;
  requestDate: string;
  requesterEmail: string;
  requesterType: 'Buyer' | 'Seller' | string;
}

export interface DisputeTableRow {
  id: string;
  disputeId: string;
  orderId: string;
  budget: number;
  reason?: string;
  requesterType: string;
  dateCreated: string;
  email: string;
  method: string;
}

const RequestsTable = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const filters = useDisputeFilters();
  const dispatch = useDispatch();
  const { search, page } = usePublicState();

  const searchValue = search?.disputeReportSearch || '';

  const {
    data: DisputeList,
    isLoading,
    isFetching,
  } = useGetDisputesReportQuery(
    {
      dateFrom: filters?.fromDate,
      dateTo: filters?.toDate,
      orderMethod: filters?.method,
      requesterRole: filters?.requesterRole,
      search: searchValue,
      page: page,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !filters?.fromDate && !filters?.toDate,
    }
  );

  const columns: TableProps<DisputeTableRow>['columns'] = [
    {
      title: t('reports.disputeId', 'Dispute ID'),
      dataIndex: 'disputeId',
      key: 'disputeId',
    },
    {
      title: t('reports.orderId', 'Order ID'),
      dataIndex: 'orderId',
      key: 'orderId',
      render: (val) => val || '-',
    },
    {
      title: t('reports.requestBudget', 'Order budget'),
      dataIndex: 'budget',
      key: 'budget',
      render: (val) => Helpers.formatMoney(val) || '-',
      minWidth: 160,
    },
    {
      title: t('reports.requesterType', 'Requester type'),
      key: 'requesterType',
      dataIndex: 'requesterType',
      render: (val) => val || '-',
      minWidth: 160,
    },
    {
      title: t('reports.requestDate', 'Request date'),
      key: 'dateCreated',
      dataIndex: 'dateCreated',
      render: (value) => {
        return (
          <p>
            {dayjs(value).isValid() ? dayjs(value).format('DD MMM YYYY') : '-'}
          </p>
        );
      },
    },
    {
      title: t('reports.requesterEmail', 'Requester email'),
      key: 'email',
      dataIndex: 'email',
      render: (val) => val || '-',
    },
    {
      title: t('reports.orderMethod', 'Order method'),
      key: 'method',
      dataIndex: 'method',
      render: (val) => val || '-',
    },
  ];

  dayjs.locale(language);

  return (
    <>
      <TableWrapper
        filters={<Filters />}
        title={t('reports.requests', 'Requests')}
        subTitle={t(
          'reports.dispute.subTitle',
          'Displaying all dispute reports in YAD system'
        )}
      >
        <CustomTable<DisputeTableRow>
          rowKey="id"
          loading={isLoading || isFetching}
          currentPage={page || 0}
          pageSize={filters?.limit || 10}
          totalElements={DisputeList?.payload?.totalElements || 0}
          onPageChange={(pageNumber, pageSize) => {
            dispatch(setPage(pageNumber - 1));
            dispatch(
              setDisputeFilters({
                limit: pageSize,
              })
            );
          }}
          onRowClick={(_, record) => {
            const id = record.id;
            dispatch(setModalId({ id }));
          }}
          columns={columns}
          dataSource={
            DisputeList?.payload?.content?.map((item) => ({
              disputeId: item?.disputeSerial,
              id: item?.disputeId,
              budget: item?.orderBudget,
              orderId: item.orderSerial,
              reason: 'asd',
              requesterType: item?.requesterType,
              dateCreated: item?.requestDate,
              email: item?.requesterEmail,
              method: item?.orderMethod,
            })) || []
          }
        />
      </TableWrapper>

      <RequestDetailsDrawer
        listOfIds={
          DisputeList?.payload?.content?.map((el) =>
            String(el.disputeId)
          ) as string[]
        }
      />
    </>
  );
};

export default RequestsTable;
